'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Sidebar from './Sidebar';
import { useAuth } from '@/lib/auth';

interface AppLayoutProps {
  children: React.ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, isLoading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!isLoading && !user) {
      router.push('/sign-up-login-screen');
    }
  }, [user, isLoading, router]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-5">
          {/* Branded D mark with spinner ring */}
          <div className="relative flex items-center justify-center w-24 h-24">
            {/* Outer spinning ring */}
            <svg
              className="absolute inset-0 w-24 h-24 animate-spin"
              viewBox="0 0 96 96"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="48" cy="48" r="44" stroke="#E2E8F0" strokeWidth="4" />
              <path
                d="M48 4 A44 44 0 0 1 92 48"
                stroke="#D97706"
                strokeWidth="4"
                strokeLinecap="round"
              />
            </svg>
            {/* Inner pulsing ring */}
            <span className="absolute inset-2 rounded-full border border-primary/20 animate-ping opacity-30" />
            {/* D logo mark */}
            <div className="relative w-16 h-16 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/30">
              <span
                className="text-3xl font-bold text-white select-none"
                style={{ fontFamily: "Georgia, 'Times New Roman', serif", letterSpacing: '-1px' }}
              >
                D
              </span>
            </div>
          </div>

          {/* Brand name */}
          <div className="text-center">
            <p className="text-xl font-bold text-foreground tracking-tight">Decor Holdings</p>
            <p className="text-xs text-muted-foreground mt-0.5 tracking-wide uppercase">Site Management Platform</p>
          </div>

          {/* Loading label */}
          <div className="flex items-center gap-2 mt-1">
            <svg className="w-4 h-4 text-primary animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <p className="text-sm text-muted-foreground">Loading your workspace…</p>
          </div>

          {/* Animated progress bar */}
          <div className="w-44 h-1 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full"
              style={{ animation: 'dh-progress 1.6s ease-in-out infinite' }}
            />
          </div>
        </div>

        <style>{`
          @keyframes dh-progress {
            0%   { width: 0%;   margin-left: 0%; }
            50%  { width: 70%;  margin-left: 15%; }
            100% { width: 0%;   margin-left: 100%; }
          }
        `}</style>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile topbar */}
        <div className="lg:hidden flex items-center gap-3 px-4 py-3 bg-card border-b border-border">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-md hover:bg-muted transition-colors"
            aria-label="Open menu"
          >
            <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <span
                className="text-sm font-bold text-white select-none"
                style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
              >
                D
              </span>
            </div>
            <span className="font-bold text-lg text-foreground tracking-tight">Decor Holdings</span>
          </div>
        </div>

        <main className="flex-1 overflow-auto">
          <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-12 py-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}